import React, { useMemo, useState } from 'react';
import { AlertTriangle, Users, ChevronRight, Download } from 'lucide-react';
import Modal from 'components/common/Modal';

const moeda = (valor) => {
  const numero = Number(valor || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(Number.isFinite(numero) ? numero : 0);
};

const ListaInadimplenciaVendedores = ({ vendedores = [] }) => {
  const [vendedorSelecionado, setVendedorSelecionado] = useState(null);

  const exportarClientesInadimplentes = (vendedor) => {
    if (!vendedor) return;

    const clientes = (vendedor.clientes_inadimplentes_lista || []).filter(Boolean);
    const linhas = [
      ['Vendedor', vendedor.nome_vendedor || ''],
      ['Desconto (R$)', Number(vendedor.value || 0).toFixed(2)],
      ['Total de clientes inadimplentes', Number(vendedor.total_clientes_inadimplentes || 0)],
      [],
      ['Clientes inadimplentes'],
      ...clientes.map((cliente) => [cliente]),
    ];

    const csv = linhas
      .map((linha) =>
        linha
          .map((coluna) => `"${String(coluna ?? '').replace(/"/g, '""')}"`)
          .join(';')
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const nomeArquivo = String(vendedor.nome_vendedor || 'vendedor')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();

    link.href = url;
    link.download = `inadimplencia_${nomeArquivo || 'vendedor'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const vendedoresOrdenados = useMemo(() => {
    return [...(Array.isArray(vendedores) ? vendedores : [])].sort(
      (a, b) => Number(b?.value || 0) - Number(a?.value || 0)
    );
  }, [vendedores]);

  const totalDesconto = useMemo(() => vendedoresOrdenados.reduce((acc, item) => acc + Number(item?.value || 0), 0), [vendedoresOrdenados]);
  const totalClientes = useMemo(
    () => vendedoresOrdenados.reduce((acc, item) => acc + Number(item?.total_clientes_inadimplentes || 0), 0),
    [vendedoresOrdenados]
  );

  if (!vendedoresOrdenados.length) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-[color:var(--status-warning-border)] bg-[color:var(--surface)] p-6 shadow-sm h-full">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_0%,rgba(245,158,11,0.13),transparent_55%)]" />
        <h3 className="relative text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center tracking-tight">
          <AlertTriangle className="h-5 w-5 text-[color:var(--status-warning-strong)] mr-2" />
          Inadimplência por vendedor
        </h3>
        <div className="relative flex flex-col items-center justify-center text-[color:var(--text-muted)] py-8 h-full">
          <div className="rounded-2xl bg-[color:var(--surface-muted)] border border-[color:var(--border)] p-4 mb-3">
            <Users className="h-10 w-10 text-[color:var(--text-muted)]" strokeWidth={1.3} />
          </div>
          <p className="font-semibold text-[color:var(--text-primary)]">Nenhum vendedor inadimplente encontrado</p>
          <p className="text-sm mt-1 text-center max-w-xs">
            Os descontos de comissão por inadimplência aparecerão aqui quando houver dados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[color:var(--status-warning-border)] bg-[color:var(--surface)] p-4 sm:p-5 shadow-sm h-full">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_80%_at_0%_0%,rgba(245,158,11,0.14),transparent_50%)]" />

      <div className="relative mb-4 sm:mb-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center tracking-tight">
            <AlertTriangle className="h-5 w-5 text-[color:var(--status-warning-strong)] mr-2" />
            Inadimplência por vendedor
          </h3>
          <span className="text-xs sm:text-sm rounded-full border border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-chip)] px-2.5 py-1 font-semibold text-[color:var(--status-warning-strong)]">
            {vendedoresOrdenados.length} vendedores
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">Desconto total</p>
            <p className="mt-0.5 font-semibold text-[color:var(--status-danger-strong)]">{moeda(totalDesconto)}</p>
          </div>
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">Clientes impactados</p>
            <p className="mt-0.5 font-semibold text-[color:var(--status-warning-strong)]">{totalClientes}</p>
          </div>
        </div>
      </div>

      <div className="relative space-y-2.5 max-h-[390px] sm:max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
        {vendedoresOrdenados.map((vendedor, index) => (
          <button
            key={`${vendedor.nome_vendedor || 'vendedor'}-${index}`}
            type="button"
            onClick={() => setVendedorSelecionado(vendedor)}
            className="group w-full rounded-xl border border-[color:var(--status-warning-border)] bg-[color:var(--surface-muted)] p-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[color:var(--status-warning-border)] hover:shadow-[0_10px_26px_rgba(245,158,11,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--status-warning-border)]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[color:var(--text-primary)] break-words text-sm sm:text-[15px] leading-tight">
                  {vendedor.nome_vendedor || 'Vendedor não identificado'}
                </p>
                <p className="text-xs text-[color:var(--text-muted)] mt-1 inline-flex items-center">
                  <Users className="h-3.5 w-3.5 mr-1" />
                  {Number(vendedor.total_clientes_inadimplentes || 0)} clientes inadimplentes
                </p>
              </div>

              <div className="shrink-0 text-right self-stretch flex flex-col items-end justify-between">
                <div className="inline-flex items-center rounded-lg bg-[color:var(--status-danger-chip)] px-2.5 py-1 text-xs font-semibold text-[color:var(--status-danger-strong)]">
                  {moeda(vendedor.value)}
                </div>
                <div className="text-[11px] text-[color:var(--status-warning-strong)]/90 inline-flex items-center font-medium">
                  Ver clientes <ChevronRight className="h-3.5 w-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Modal
        isOpen={Boolean(vendedorSelecionado)}
        onClose={() => setVendedorSelecionado(null)}
        title="Clientes inadimplentes"
        size="lg"
      >
        {vendedorSelecionado && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-wash)] p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-semibold text-[color:var(--text-primary)]">{vendedorSelecionado.nome_vendedor}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-sm">
                    <span className="inline-flex items-center rounded-full bg-[color:var(--status-warning-chip)] px-2.5 py-1 font-medium text-[color:var(--status-warning-strong)]">
                      {Number(vendedorSelecionado.total_clientes_inadimplentes || 0)} clientes
                    </span>
                    <span className="inline-flex items-center rounded-full bg-[color:var(--status-danger-chip)] px-2.5 py-1 font-medium text-[color:var(--status-danger-strong)]">
                      Desconto: {moeda(vendedorSelecionado.value)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => exportarClientesInadimplentes(vendedorSelecionado)}
                  className="inline-flex items-center justify-center rounded-lg border border-[color:var(--status-warning-border)] bg-[color:var(--surface)] px-3 py-2 text-sm font-medium text-[color:var(--status-warning-strong)] transition-colors hover:bg-[color:var(--status-warning-wash)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--status-warning-border)]"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Exportar lista
                </button>
              </div>
            </div>

            <div className="max-h-[360px] overflow-y-auto pr-1 custom-scrollbar">
              <ul className="space-y-2">
                {(vendedorSelecionado.clientes_inadimplentes_lista || [])
                  .filter(Boolean)
                  .map((cliente, idx) => (
                    <li
                      key={`${cliente}-${idx}`}
                      className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)]"
                    >
                      {cliente}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(148, 163, 184, 0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.65); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.9); }
      `}</style>
    </div>
  );
};

export default ListaInadimplenciaVendedores;
